/**
 * The compliance checks, as data.
 *
 * Deliberately pure and free of React, GitHub and Backstage APIs: the thresholds
 * are the part people argue about, and they should be readable and testable
 * without tracing a component. Adding a check is an entry in RULES.
 *
 * Semantics are lifted from the Port scorecards this replaces
 * (port-infrastructure/port/scorecards/githubRepository and the
 * calculationProperties on port/blueprints/githubRepository.json) rather than
 * reinvented, so a repo's result does not change as it moves between portals.
 * Several of them are not what a reasonable guess would produce - see the
 * comments on the individual rules.
 */

/** Which of GitHub's two protection systems a fact came from. */
export type ProtectionSource = 'classic' | 'ruleset';

/**
 * One effective rule from GET /repos/{owner}/{repo}/rules/branches/{branch}.
 *
 * That endpoint returns the rules ACTUALLY IN FORCE on a branch, merged across
 * every ruleset that targets it - repository-level and organisation-level alike.
 * The same type can therefore appear more than once (two rulesets both blocking
 * force pushes), which is why mergeProtection combines rather than picks.
 */
export type RulesetRule = {
  type: string;
  parameters?: Record<string, unknown>;
};

/** Everything the checks are evaluated against. */
export type ComplianceFacts = {
  /** Repo name with the org stripped, i.e. the `last` of a `org/name` split. */
  name: string;
  defaultBranch: string;
  topics: string[];
  hasCodeowners: boolean;
  /**
   * Protection on the default branch, or undefined when there is none from
   * EITHER of GitHub's two systems.
   *
   * GitHub has classic branch protection and the newer rulesets, and a repo can
   * be protected by either or both. They are separate APIs and the classic one
   * cannot see rulesets at all: a repo protected purely by rulesets answers
   * /branches/{branch}/protection with 404 "Branch not protected".
   *
   * Reading only the classic endpoint therefore reports every protection check
   * as FAILING on a properly protected repo - not a gap, a false negative. This
   * is a merge of both, built by mergeProtection().
   *
   * Undefined still means genuinely unprotected, which IS the answer to "is it
   * protected" rather than a missing value.
   */
  protection?: {
    requiredApprovingReviewCount: number;
    requireCodeOwnerReviews: boolean;
    allowForcePushes: boolean;
    requireConversationResolution: boolean;
    requireSignedCommits: boolean;
    requireLinearHistory: boolean;
    /** Which system(s) provided this, for the card to show. */
    sources: ProtectionSource[];
  };
  /** spec.owner from the catalog entity. Not a GitHub fact. */
  owner?: string;
  /** System resolved from the entity or its ServiceGroup, never from repo topics. */
  system?: string;
};

/** Conventions that vary by organisation, so they are configurable. */
export type ComplianceConventions = {
  /** Regex the repo name must match. */
  namingPattern: string;
  /** Topic prefix identifying the owning system, e.g. `system-`. */
  systemTopicPrefix: string;
  /** Regex identifying a service topic. */
  serviceTopicPattern: string;
  /**
   * System name that does not count as a real system.
   *
   * There is a `system-temp-placeholder` topic in use, and it must NOT satisfy
   * the prefix rule - otherwise every placeholder repo reads as compliant.
   */
  placeholderSystem: string;
};

export const DEFAULT_CONVENTIONS: ComplianceConventions = {
  namingPattern: '^[a-z0-9]+(-[a-z0-9]+)*$',
  systemTopicPrefix: 'system-',
  serviceTopicPattern: '^service(group)?-',
  placeholderSystem: 'temp-placeholder',
};

/**
 * How much a failure matters.
 *
 * A flat pass/fail list loses real information: "no owning team" and "does not
 * require signed commits" are not the same problem, and somebody triaging needs
 * to know which to fix first. Maps onto Port's Bronze/Silver/Gold.
 */
export type Tier = 'required' | 'recommended' | 'advanced';

export type Rule = {
  id: string;
  title: string;
  tier: Tier;
  /** Shown when the check fails, so the fix is stated rather than inferred. */
  remedy: string;
  evaluate: (facts: ComplianceFacts, conv: ComplianceConventions) => boolean;
};

/**
 * All real systems represented by the repository's topics.
 */
export function systemsFromTopics(
  topics: string[],
  conv: ComplianceConventions,
): string[] {
  return topics
    .filter(t => t.startsWith(conv.systemTopicPrefix))
    .map(t => t.slice(conv.systemTopicPrefix.length))
    .filter(t => t && t !== conv.placeholderSystem);
}

/** The classic /branches/{branch}/protection response, in the shape we use. */
export type ClassicProtection = {
  requiredApprovingReviewCount: number;
  requireCodeOwnerReviews: boolean;
  allowForcePushes: boolean;
  requireConversationResolution: boolean;
  requireSignedCommits: boolean;
  requireLinearHistory: boolean;
};

/**
 * Combines GitHub's two protection systems into one answer per check.
 *
 * A repo can be protected by classic branch protection, by rulesets, or by both,
 * and the two APIs cannot see each other. A rule is satisfied if EITHER system
 * enforces it - that is what "is this branch protected" means to a human, and
 * anything stricter reports false negatives on repos that are properly locked
 * down.
 *
 * Returns undefined only when neither system protects the branch at all.
 *
 * Ruleset equivalents, which are not always obvious:
 *
 *   force pushes disabled     <- a `non_fast_forward` rule exists
 *   conversation resolution   <- pull_request.required_review_thread_resolution
 *   code owner review         <- pull_request.require_code_owner_review
 *                                (singular; the classic field is plural)
 *
 * Several rulesets can target one branch, so booleans are OR-ed and the review
 * count takes the MAX: if any ruleset demands two approvals, two are required.
 */
export function mergeProtection(
  classic: ClassicProtection | undefined,
  rules: RulesetRule[],
): ComplianceFacts['protection'] {
  if (!classic && rules.length === 0) {
    return undefined;
  }

  const sources: ProtectionSource[] = [];
  if (classic) sources.push('classic');
  if (rules.length > 0) sources.push('ruleset');

  const has = (type: string) => rules.some(r => r.type === type);
  const prParams = rules
    .filter(r => r.type === 'pull_request')
    .map(r => r.parameters ?? {});

  const prBool = (key: string) => prParams.some(params => Boolean(params[key]));
  const prMaxNumber = (key: string) =>
    prParams.reduce((max, params) => {
      const value = params[key];
      return typeof value === 'number' && value > max ? value : max;
    }, 0);

  return {
    requiredApprovingReviewCount: Math.max(
      classic?.requiredApprovingReviewCount ?? 0,
      prMaxNumber('required_approving_review_count'),
    ),
    requireCodeOwnerReviews:
      Boolean(classic?.requireCodeOwnerReviews) ||
      prBool('require_code_owner_review'),
    // Inverted relative to the others: classic reports whether force pushes are
    // ALLOWED, while a ruleset expresses the same thing as the presence of a
    // rule blocking them. Allowed only if nothing anywhere blocks it.
    allowForcePushes:
      (classic ? classic.allowForcePushes : true) && !has('non_fast_forward'),
    requireConversationResolution:
      Boolean(classic?.requireConversationResolution) ||
      prBool('required_review_thread_resolution'),
    requireSignedCommits:
      Boolean(classic?.requireSignedCommits) || has('required_signatures'),
    requireLinearHistory:
      Boolean(classic?.requireLinearHistory) || has('required_linear_history'),
    sources,
  };
}

export const RULES: Rule[] = [
  // ---------------------------------------------------------------------------
  // Required - everything else in the catalog depends on these
  // ---------------------------------------------------------------------------
  {
    id: 'has-owner',
    title: 'Has an owning team',
    tier: 'required',
    remedy: 'Set spec.owner in catalog-info.yaml to a group that exists.',
    // Port calls this `$team`. It is first because ownership is what every other
    // view in Backstage keys off - an unowned entity is invisible in practice.
    evaluate: f => Boolean(f.owner),
  },
  {
    id: 'default-branch-main',
    title: 'Default branch is "main"',
    tier: 'required',
    remedy: 'Rename the default branch to main in the repository settings.',
    evaluate: f => f.defaultBranch === 'main',
  },
  {
    id: 'branch-protected',
    title: 'Default branch is protected',
    tier: 'required',
    remedy: 'Add a branch protection rule for the default branch.',
    evaluate: f => Boolean(f.protection),
  },
  {
    id: 'requires-reviewer',
    title: 'Requires at least one reviewer',
    tier: 'required',
    remedy:
      'Set "Require approvals" to 1 or more on the branch protection rule.',
    evaluate: f => (f.protection?.requiredApprovingReviewCount ?? 0) >= 1,
  },
  {
    id: 'has-codeowners',
    title: 'CODEOWNERS file present',
    tier: 'required',
    remedy: 'Add a CODEOWNERS file at the repo root, in .github/ or in docs/.',
    evaluate: f => f.hasCodeowners,
  },

  // ---------------------------------------------------------------------------
  // Recommended
  // ---------------------------------------------------------------------------
  {
    id: 'requires-codeowner-review',
    title: 'Requires code owner review',
    tier: 'recommended',
    remedy:
      'Enable "Require review from Code Owners" on the branch protection rule.',
    evaluate: f => Boolean(f.protection?.requireCodeOwnerReviews),
  },
  {
    id: 'force-pushes-disabled',
    title: 'Force pushes disabled',
    tier: 'recommended',
    remedy: 'Disable "Allow force pushes" on the branch protection rule.',
    // Unprotected counts as a failure rather than as unknown: with no protection
    // rule, force pushes are possible, which is what the check is really about.
    evaluate: f => f.protection !== undefined && !f.protection.allowForcePushes,
  },
  {
    id: 'has-topics',
    title: 'Has GitHub topics applied',
    tier: 'recommended',
    remedy:
      'Add topics to the repository, including one system- and one service- topic.',
    evaluate: f => f.topics.length > 0,
  },
  {
    id: 'topic-conventions',
    title: 'Topic conventions followed',
    tier: 'recommended',
    remedy:
      "Apply a system-<name> topic for this service's system, at least one service-<name> or " +
      'servicegroup-<name> topic, and keep all topics lowercase alphanumeric with hyphens.',
    evaluate: (f, conv) => {
      const systems = systemsFromTopics(f.topics, conv);
      const serviceCount = f.topics.filter(t =>
        new RegExp(conv.serviceTopicPattern).test(t),
      ).length;
      const wellNamed = f.topics.every(t => /^[a-z0-9-]+$/.test(t));
      return Boolean(
        f.system &&
          systems.includes(f.system) &&
          serviceCount >= 1 &&
          wellNamed,
      );
    },
  },
  {
    id: 'naming-convention',
    title: 'Repository naming convention',
    tier: 'recommended',
    remedy: 'Rename the repository to lowercase kebab-case.',
    evaluate: (f, conv) => new RegExp(conv.namingPattern).test(f.name),
  },
  {
    id: 'system-prefix',
    title: 'Name prefixed with its system',
    tier: 'recommended',
    remedy:
      'Name the repository <system> or <system>-<something>, matching its system- topic.',
    // Equality counts, not just prefixing: a repo named exactly after its system
    // satisfies this. Missing or placeholder system fails.
    evaluate: (f, conv) => {
      return systemsFromTopics(f.topics, conv).some(
        system => f.name === system || f.name.startsWith(`${system}-`),
      );
    },
  },

  // ---------------------------------------------------------------------------
  // Advanced
  // ---------------------------------------------------------------------------
  {
    id: 'conversation-resolution',
    title: 'Requires conversation resolution',
    tier: 'advanced',
    remedy: 'Enable "Require conversation resolution before merging".',
    evaluate: f => Boolean(f.protection?.requireConversationResolution),
  },
  {
    id: 'signed-commits',
    title: 'Requires signed commits',
    tier: 'advanced',
    remedy: 'Enable "Require signed commits" on the branch protection rule.',
    evaluate: f => Boolean(f.protection?.requireSignedCommits),
  },
  {
    id: 'linear-history',
    title: 'Requires linear history',
    tier: 'advanced',
    remedy: 'Enable "Require linear history" on the branch protection rule.',
    evaluate: f => Boolean(f.protection?.requireLinearHistory),
  },
];

export type RuleResult = Rule & { passed: boolean };

export function evaluateRules(
  facts: ComplianceFacts,
  conv: ComplianceConventions = DEFAULT_CONVENTIONS,
): RuleResult[] {
  return RULES.map(rule => ({ ...rule, passed: rule.evaluate(facts, conv) }));
}

/** Pass counts per tier, for the card's summary line. */
export function summarise(results: RuleResult[]) {
  const tiers: Tier[] = ['required', 'recommended', 'advanced'];
  return tiers.map(tier => {
    const inTier = results.filter(r => r.tier === tier);
    return {
      tier,
      passed: inTier.filter(r => r.passed).length,
      total: inTier.length,
    };
  });
}
